import React, { useState } from 'react';

import { formatPriceWithCurrency } from '@vb-django/components/minskog/utils';

import { Button, Dialog, Icon, Link, Text } from '@ui-component-library';

import { useCheckout } from '../context/CheckoutContext';

import { SelectProduct } from './SelectProduct/SelectProduct';

export function OrderSummary() {
  const { selectedProducts, orderPrices, couponDiscount, paymentMethod, extraData, config } =
    useCheckout();
  const isDronePurchase = config.type === 'drone';
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const hasSelected = selectedProducts.length > 0;
  const totalArea = selectedProducts.reduce(
    (acc, product) => acc + ((product?.area as number) ?? 0),
    0
  );
  const selectedPackage = extraData.selectedPackage;
  const selectedForestManagementPlan = extraData.selectedForestManagementPlan;
  return (
    <>
      <div className="flex w-full flex-col gap-4">
        <Text variant="text-base/leading-normal/medium" className="pl-4">
          Sammanfattning
        </Text>
        <div className="bg-white-50 rounded-xl p-4">
          <div className={hasSelected ? 'flex flex-col gap-4' : 'flex flex-col'}>
            <SelectProduct showSummary={true} />

            {hasSelected && (
              <>
                <div className="h-[0.5px] w-full border border-solid border-stone-500/17" />

                {!isDronePurchase && (
                  <div className="flex flex-row items-start justify-start gap-2">
                    <div className="flex flex-col gap-1">
                      <Text variant="text-sm/leading-normal/medium">
                        {config.summaryProductLabel}
                      </Text>
                      <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                        {formatPriceWithCurrency(
                          orderPrices.totalPriceBeforeDiscount
                            ? orderPrices.totalPriceBeforeDiscount
                            : orderPrices.propertiesTotalPrice
                        )}
                      </Text>
                    </div>
                  </div>
                )}
                {config.summaryAdditionalContent}
                <div className="h-[0.5px] w-full border border-solid border-stone-500/17" />
                <div className="flex flex-row items-center justify-between gap-2">
                  <div className="flex flex-col gap-1">
                    <Text variant="text-sm/leading-normal/medium">Totalbelopp</Text>
                    <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
                      {formatPriceWithCurrency(orderPrices.discountPrice)}
                    </Text>
                  </div>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setIsDetailsOpen(true)}
                  >
                    Detaljer
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {hasSelected && (
        <>
          <div className="h-[0.5px] w-full border border-solid border-stone-500/17" />

          <div className="flex w-full flex-col gap-4">
            <Text variant="text-base/leading-normal/medium">Betalningsmetod</Text>
            <div className="bg-white-50 flex w-full flex-col gap-4 rounded-xl p-4">
              {paymentMethod === 'klarna' && (
                <div className="flex flex-col gap-4">
                  <img
                    alt="Klarna logo"
                    width="60px"
                    height="24px"
                    src={`${Window.static_path}img/payment-provider/klarna.png`}
                  />
                  <div className="flex flex-row gap-2 rounded-md bg-stone-100 p-3">
                    <Icon name="InfoFilled" size="medium" className="fill-pebble-500" />
                    <div className="flex flex-col gap-2">
                      <Text variant="text-xs/leading-normal/normal" className="text-pebble-500">
                        Köp via Klarna visas med 25% moms inkluderad. Som företag kan du ändå göra
                        fullt momsavdrag även om det inte syns vid checkout.
                        <br />
                        Du kan klicka på <b>Detaljer</b> ovan för att se mer information.
                      </Text>
                    </div>
                  </div>

                  <Text variant="text-xs/leading-normal/normal">
                    Läs mer hos{' '}
                    <Link href="https://www.klarna.com/se/" target="blank" underline>
                      Klarna.com/se
                    </Link>
                    {', '}
                    <Link href="https://www.klarna.com/se/villkor/" target="blank" underline>
                      Klarnas köpvillkor
                    </Link>
                    {', '}
                    <Link href="https://www.klarna.com/se/dataskydd/" target="blank" underline>
                      Klarnas sekretessmeddelande
                    </Link>
                    {' och '}
                    <Link href="https://www.klarna.com/se/cookies/" target="blank" underline>
                      Klarnas cookie-meddelande
                    </Link>
                  </Text>

                  <div className="h-[0.5px] w-full border border-stone-500/17" />

                  <div className="flex flex-row gap-2">
                    <Text variant="text-xs/leading-normal/normal">
                      <Link href="https://treebula.se/terms/" target="blank" underline>
                        Treebulas användarvillkor
                      </Link>
                      {', '}
                      <Link href="https://treebula.se/privacy/" target="blank" underline>
                        Treebulas integritetspolicy
                      </Link>
                      .
                    </Text>
                  </div>
                </div>
              )}
              {paymentMethod === 'invoice' && (
                <div className="flex flex-col gap-4">
                  <div className="bg-charcoal-500 text-white-50 flex w-fit flex-row rounded-md px-2 py-1">
                    <Text variant="text-xs/leading-normal/medium">Faktura</Text>
                  </div>
                  <div className="flex flex-row gap-2 rounded-md bg-stone-100 p-3">
                    <Icon name="InfoFilled" size="medium" className="fill-pebble-500" />
                    <div className="flex flex-col gap-2">
                      <Text variant="text-xs/leading-normal/normal" className="text-pebble-500">
                        Belopp över 20,000 kr betalas via faktura. Vi kontaktar dig inom kort
                      </Text>
                    </div>
                  </div>

                  <div className="h-[0.5px] w-full border border-stone-500/17" />

                  <div className="flex flex-row gap-2">
                    <Text variant="text-xs/leading-normal/normal">
                      <Link href="https://treebula.se/terms/" target="blank" underline>
                        Treebulas användarvillkor
                      </Link>
                      {', '}
                      <Link href="https://treebula.se/privacy/" target="blank" underline>
                        Treebulas integritetspolicy
                      </Link>
                      .
                    </Text>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {/* Price details dialog */}
      <Dialog isOpen={isDetailsOpen} size="small">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="text-base/leading-normal/medium">Prisdetaljer</Text>
            <Button variant="secondary" size="icon" onClick={() => setIsDetailsOpen(false)}>
              <Icon name="CrossSimple" className="color-charcoal-500" size="medium" />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className="flex h-full flex-col gap-4">
            <div className="flex flex-col gap-4">
              {isDronePurchase && totalArea <= 30 && (
                <div className="flex flex-row gap-2 rounded-md bg-stone-100 p-3">
                  <Icon name="InfoFilled" size="medium" />
                  <div className="flex flex-col gap-2">
                    <Text variant="text-xs/leading-normal/normal">
                      För att kunna genomföra en kostnadseffektiv pilotutkörning och täcka
                      resekostnader samt övriga fasta moment i uppstarten, kan vi tyvärr aldrig ta
                      betalt för mindre än 30 hektar
                    </Text>
                  </div>
                </div>
              )}
              {isDronePurchase && (
                <>
                  <div className="flex flex-row items-center justify-between gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      {`Ortofoto (${String(orderPrices.propertiesHectarePrice ?? '')}/ha)`}
                    </Text>
                    <Text variant="text-sm/leading-normal/medium">
                      {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                    </Text>
                  </div>
                  {(selectedPackage || selectedForestManagementPlan) && (
                    <div className="h-[0.5px] w-full border border-stone-500/17" />
                  )}
                  {selectedPackage && (
                    <div className="flex flex-row items-center justify-between gap-2">
                      <Text variant="text-sm/leading-normal/normal">
                        {`Skadeanalys ${selectedPackage.title ?? ''} (${String(orderPrices.packageHectarePrice ?? '')}/ha)`}
                      </Text>
                      <Text variant="text-sm/leading-normal/medium">
                        {formatPriceWithCurrency(orderPrices.packageTotalPrice)}
                      </Text>
                    </div>
                  )}
                  {selectedForestManagementPlan && (
                    <div className="flex flex-row items-center justify-between gap-2">
                      <Text variant="text-sm/leading-normal/normal">Skogsbruksplan (99 kr/ha)</Text>
                      <Text variant="text-sm/leading-normal/medium">
                        {formatPriceWithCurrency(orderPrices.forestManagementPlanTotalPrice)}
                      </Text>
                    </div>
                  )}
                  <div className="h-[0.5px] w-full border border-stone-500/17" />
                </>
              )}
              {couponDiscount && (
                <div className="flex flex-row items-center justify-between gap-2">
                  <Text variant="text-sm/leading-normal/normal">
                    Rabattkod {couponDiscount.coupon_code}
                  </Text>
                  <Text variant="text-sm/leading-normal/medium">
                    -{formatPriceWithCurrency(couponDiscount.coupon_discount_price)}
                  </Text>
                </div>
              )}
              <div className="flex flex-row items-center justify-between gap-2">
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                  Moms
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.tax)}
                </Text>
              </div>
              <div className="flex flex-row items-center justify-between gap-2">
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-300">
                  Inkl. moms
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(
                    (orderPrices.discountPrice ?? 0) + (orderPrices.tax ?? 0)
                  )}
                </Text>
              </div>
            </div>
            <div className="mt-auto h-[0.5px] w-full border border-stone-500/17" />
            <div className="flex flex-row items-center justify-between gap-2">
              <Text variant="text-sm/leading-normal/medium">Exkl. moms</Text>
              <Text variant="text-sm/leading-normal/medium">
                {formatPriceWithCurrency(orderPrices.discountPrice)}
              </Text>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
